// ============================================================================
// SS3 CHRISTIAN RELIGIOUS STUDIES — COMPLETE LEARNING MODULE (8 TOPICS)
// NERDC Scheme of Work — First Term only; no Second or Third Term in NERDC scheme
// ============================================================================

export const WAEC_CRS_LEARN_SS3 = [
  {
    "topic": "Civic Responsibilities",
    "topicCode": "SS3-CRS-01",
    "module": "Christian Living and National Life",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Civic responsibilities</span> are the duties every citizen owes to the state and to fellow citizens, so that society can function peacefully and develop. The Bible teaches that Christians, as citizens of both heaven and earth, must take these duties seriously.</div><h3 class=\"learn-subheading\">1. Meaning of Civic Responsibilities</h3><p class=\"learn-p\">Civic responsibilities are the obligations a citizen owes to their country and community, arising from being a member of that society and enjoying its benefits and protection.</p><h3 class=\"learn-subheading\">2. Examples of Civic Responsibilities</h3><ul style=\"margin-left:20px;\"><li>Obedience to the laws of the land.</li><li>Payment of taxes.</li><li>Voting and participating in elections.</li><li>Community service and voluntary work.</li><li>Protecting public property and the environment.</li><li>Respecting the rights of others.</li><li>Defending the nation when necessary.</li></ul><h3 class=\"learn-subheading\">3. Importance of Civic Responsibilities in Nation Building</h3><ul style=\"margin-left:20px;\"><li>They promote peace, order and security in society.</li><li>They provide government with revenue (through taxes) for development projects.</li><li>They strengthen democracy through active citizen participation.</li><li>They build a sense of national unity and belonging.</li><li>They reduce crime and social vices.</li><li>They ensure that public resources and amenities are protected and maintained.</li></ul><h3 class=\"learn-subheading\">4. Biblical Teaching on Civic Duties — Romans 13:1–7</h3><p class=\"learn-p\">In Romans 13:1–7, Paul teaches that every person should submit to governing authorities, since all authority is established by God, and those who resist it are resisting what God has instituted. Christians are to obey the law, pay taxes, and give honour and respect to whom they are due, not merely out of fear of punishment but for the sake of conscience.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Be ready to quote and explain Romans 13:1–7 as the key biblical passage supporting civic responsibility and submission to government.</div>",
    "questions": [
      {
        "q": "Civic responsibilities are best defined as:",
        "o": [
          "duties a citizen owes to the state and fellow citizens",
          "rules that apply only in church",
          "privileges granted only to leaders",
          "laws that apply only to foreigners"
        ],
        "a": 0,
        "e": "They arise from being a member of society.",
        "h": "Think of obligations, not privileges.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is an example of a civic responsibility?",
        "o": [
          "Refusing to vote",
          "Ignoring community needs",
          "Avoiding all public gatherings",
          "Payment of taxes"
        ],
        "a": 3,
        "e": "Taxes fund government services.",
        "h": "A financial duty to the state.",
        "yr": "SS3"
      },
      {
        "q": "Voting in elections is an example of:",
        "o": [
          "a punishment",
          "a civic responsibility",
          "a religious ritual",
          "a foreign policy"
        ],
        "a": 1,
        "e": "It is part of democratic participation.",
        "h": "Exercising a citizen's right and duty.",
        "yr": "SS3"
      },
      {
        "q": "Community service is considered a civic responsibility because it:",
        "o": [
          "has no value to the nation",
          "benefits only the individual",
          "benefits the wider society",
          "is required only of government workers"
        ],
        "a": 2,
        "e": "It contributes to the common good.",
        "h": "Helping others in the community.",
        "yr": "SS3"
      },
      {
        "q": "Obedience to the laws of the land is important because it:",
        "o": [
          "restricts personal freedom unfairly",
          "applies only to criminals",
          "is optional for believers",
          "maintains order and security in society"
        ],
        "a": 3,
        "e": "Laws exist to protect everyone.",
        "h": "Keeps peace and order.",
        "yr": "SS3"
      },
      {
        "q": "Civic responsibilities help nation building mainly by:",
        "o": [
          "promoting peace, order and development",
          "increasing poverty",
          "weakening democracy",
          "encouraging disobedience"
        ],
        "a": 0,
        "e": "They strengthen the structures of society.",
        "h": "Positive contribution to the nation.",
        "yr": "SS3"
      },
      {
        "q": "Which book and chapter is the key Bible passage on civic duties in this topic?",
        "o": [
          "1 Timothy 6:6–10",
          "Genesis 2:15",
          "Romans 13:1–7",
          "Matthew 18:21–22"
        ],
        "a": 2,
        "e": "Paul teaches submission to governing authorities here.",
        "h": "A New Testament letter to Rome.",
        "yr": "SS3"
      },
      {
        "q": "According to Romans 13, governing authorities are:",
        "o": [
          "irrelevant to Christians",
          "only valid in Israel",
          "established by God",
          "opposed to God's will"
        ],
        "a": 2,
        "e": "Paul says all authority is instituted by God.",
        "h": "Their source is divine.",
        "yr": "SS3"
      },
      {
        "q": "Romans 13 teaches that resisting lawful authority is:",
        "o": [
          "always praiseworthy",
          "irrelevant to Christian life",
          "resisting what God has instituted",
          "a sign of true faith"
        ],
        "a": 2,
        "e": "Paul links civil authority to God's order.",
        "h": "A serious spiritual matter.",
        "yr": "SS3"
      },
      {
        "q": "According to Romans 13:1–7, believers should obey the law:",
        "o": [
          "for the sake of conscience, not just fear of punishment",
          "only to avoid prison",
          "only when convenient",
          "only when no one is watching"
        ],
        "a": 0,
        "e": "Inner conviction, not only fear, should motivate obedience.",
        "h": "Internal motivation matters.",
        "yr": "SS3"
      },
      {
        "q": "Paying taxes, according to Romans 13, is an obligation because:",
        "o": [
          "taxes are unbiblical",
          "authorities are God's servants attending to governance",
          "taxes fund only religious activities",
          "only rich people should pay"
        ],
        "a": 1,
        "e": "Paul links tax payment to respecting government's role.",
        "h": "Government service requires funding.",
        "yr": "SS3"
      },
      {
        "q": "Protecting public property is an example of:",
        "o": [
          "civic responsibility",
          "criminal activity",
          "religious ritual",
          "personal hobby"
        ],
        "a": 0,
        "e": "It preserves shared community resources.",
        "h": "Caring for shared assets.",
        "yr": "SS3"
      },
      {
        "q": "A citizen who refuses to pay taxes but enjoys government services is failing in:",
        "o": [
          "personal hygiene",
          "family loyalty",
          "civic responsibility",
          "private worship"
        ],
        "a": 2,
        "e": "They benefit without contributing.",
        "h": "Not fulfilling a civic duty.",
        "yr": "SS3"
      },
      {
        "q": "Respecting the rights of others is an example of:",
        "o": [
          "a religious sacrament",
          "civic responsibility",
          "an economic policy",
          "a foreign treaty"
        ],
        "a": 1,
        "e": "It supports peaceful coexistence.",
        "h": "Mutual respect among citizens.",
        "yr": "SS3"
      },
      {
        "q": "Defending the nation when necessary is considered:",
        "o": [
          "a purely military matter unrelated to citizens",
          "irrelevant to Christian teaching",
          "a civic responsibility",
          "an optional hobby"
        ],
        "a": 2,
        "e": "Citizens may be called to serve their country.",
        "h": "National duty in times of need.",
        "yr": "SS3"
      },
      {
        "q": "Civic responsibilities contribute to national unity by:",
        "o": [
          "dividing people by region",
          "promoting selfishness",
          "building a sense of belonging among citizens",
          "weakening community ties"
        ],
        "a": 2,
        "e": "Shared duties foster common identity.",
        "h": "Bringing people together.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best completes Romans 13's teaching: Christians should give honour to whom honour is due, this includes:",
        "o": [
          "avoiding all forms of government",
          "obeying only religious leaders",
          "respecting legitimate authorities",
          "ignoring all leaders"
        ],
        "a": 2,
        "e": "Paul calls for appropriate respect toward those in authority.",
        "h": "Respect where it's due.",
        "yr": "SS3"
      },
      {
        "q": "A reduction in crime and social vices is a benefit of:",
        "o": [
          "high unemployment",
          "weak law enforcement",
          "citizens fulfilling their civic responsibilities",
          "citizens ignoring the law"
        ],
        "a": 2,
        "e": "Responsible citizenship supports lawfulness.",
        "h": "Positive outcome of good citizenship.",
        "yr": "SS3"
      },
      {
        "q": "Civic responsibilities are owed to:",
        "o": [
          "only one's immediate family",
          "only religious leaders",
          "only foreign governments",
          "the state and the community"
        ],
        "a": 3,
        "e": "They extend beyond the individual's household.",
        "h": "Society as a whole.",
        "yr": "SS3"
      },
      {
        "q": "Romans 13:1-7 primarily addresses the relationship between:",
        "o": [
          "husbands and wives",
          "parents and children",
          "employers and employees",
          "Christians and governing authorities"
        ],
        "a": 3,
        "e": "It focuses on submission to civil government.",
        "h": "Citizens and the state.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "HIV/AIDS I",
    "topicCode": "SS3-CRS-02",
    "module": "Health, Morality and Christian Living",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">HIV</span> (Human Immunodeficiency Virus) is a virus that attacks the body's immune system, while <span class=\"learn-keyword\">AIDS</span> (Acquired Immune Deficiency Syndrome) is the advanced stage of HIV infection, when the immune system is severely weakened.</div><h3 class=\"learn-subheading\">1. Meaning of HIV and AIDS</h3><p class=\"learn-p\">HIV is the virus that causes infection; AIDS is the final, most severe stage of the disease it causes, where the body can no longer fight off infections and illnesses effectively.</p><h3 class=\"learn-subheading\">2. Causes and Modes of Transmission</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Mode of transmission</th><th>Explanation</th></tr></thead><tbody><tr><td>Unprotected sexual intercourse</td><td>With an infected person, the most common mode worldwide</td></tr><tr><td>Blood transfusion</td><td>With infected, unscreened blood</td></tr><tr><td>Sharing sharp objects</td><td>Needles, syringes, razor blades or other objects contaminated with infected blood</td></tr><tr><td>Mother-to-child transmission</td><td>During pregnancy, childbirth, or breastfeeding</td></tr></tbody></table></div><p class=\"learn-p\">HIV is <strong>not</strong> transmitted by casual contact such as hugging, shaking hands, sharing food, or mosquito bites.</p><h3 class=\"learn-subheading\">3. Biblical Perspectives on Sexual Purity</h3><p class=\"learn-p\">The Bible consistently teaches sexual purity and faithfulness. 1 Corinthians 6:18–20 instructs believers to flee sexual immorality, since the body is a temple of the Holy Spirit. Hebrews 13:4 teaches that marriage should be honoured and the marriage bed kept pure. These teachings directly support behaviour that reduces the risk of HIV transmission.</p><h3 class=\"learn-subheading\">4. Consequences of Immoral Living</h3><ul style=\"margin-left:20px;\"><li>Increased risk of contracting HIV and other sexually transmitted infections.</li><li>Broken relationships, families and trust.</li><li>Guilt, shame and spiritual separation from God.</li><li>Social stigma and its effects on mental health.</li><li>Unwanted pregnancy and its related challenges.</li></ul><h3 class=\"learn-subheading\">5. Preventive Measures</h3><ul style=\"margin-left:20px;\"><li>Abstinence from premarital sex and faithfulness within marriage.</li><li>Avoiding the sharing of sharp objects such as needles and razors.</li><li>Screening of blood before transfusion.</li><li>Use of sterile medical equipment.</li><li>Voluntary counselling and testing (VCT) to know one's status.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Distinguish clearly between how HIV IS transmitted and common myths about how it is NOT transmitted.</div>",
    "questions": [
      {
        "q": "HIV stands for:",
        "o": [
          "Human Immune Virus Infection",
          "Human Infectious Viral Infection",
          "Highly Infectious Viral Illness",
          "Human Immunodeficiency Virus"
        ],
        "a": 3,
        "e": "It is the virus that attacks the immune system.",
        "h": "Think of the full technical name.",
        "yr": "SS3"
      },
      {
        "q": "AIDS stands for:",
        "o": [
          "Acquired Immune Deficiency Syndrome",
          "Acute Infectious Disease Syndrome",
          "Advanced Immune Disorder Syndrome",
          "Acquired Infectious Deficiency Sickness"
        ],
        "a": 0,
        "e": "It is the advanced stage of HIV infection.",
        "h": "A syndrome, not a virus itself.",
        "yr": "SS3"
      },
      {
        "q": "AIDS is best described as:",
        "o": [
          "a completely different disease from HIV",
          "a bacterial infection",
          "the advanced stage of HIV infection",
          "a type of cancer unrelated to HIV"
        ],
        "a": 2,
        "e": "AIDS develops when HIV severely weakens the immune system.",
        "h": "Progression of the same condition.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a common mode of HIV transmission?",
        "o": [
          "Unprotected sexual intercourse",
          "Sharing a meal with an infected person",
          "Hugging an infected person",
          "Mosquito bites"
        ],
        "a": 0,
        "e": "It is the most common mode worldwide.",
        "h": "Direct bodily fluid contact.",
        "yr": "SS3"
      },
      {
        "q": "HIV can be transmitted through:",
        "o": [
          "sharing sharp objects contaminated with infected blood",
          "sharing textbooks",
          "swimming in a public pool",
          "shaking hands"
        ],
        "a": 0,
        "e": "Needles and razors can carry infected blood.",
        "h": "Blood-to-blood contact.",
        "yr": "SS3"
      },
      {
        "q": "Mother-to-child transmission of HIV can occur during:",
        "o": [
          "shared classroom use",
          "pregnancy, childbirth or breastfeeding",
          "normal daily conversation",
          "handshakes"
        ],
        "a": 1,
        "e": "The virus can pass from mother to child.",
        "h": "Related to childbirth and nursing.",
        "yr": "SS3"
      },
      {
        "q": "Blood transfusion can transmit HIV if the blood is:",
        "o": [
          "donated by a healthy person",
          "unscreened and infected",
          "screened and certified safe",
          "boiled before use"
        ],
        "a": 1,
        "e": "Screening prevents this mode of transmission.",
        "h": "Lack of proper testing.",
        "yr": "SS3"
      },
      {
        "q": "Which Bible passage teaches believers to flee sexual immorality?",
        "o": [
          "1 Corinthians 6:18–20",
          "Romans 13:1–7",
          "Proverbs 22:29",
          "Genesis 2:15"
        ],
        "a": 0,
        "e": "Paul teaches that the body is a temple of the Holy Spirit.",
        "h": "A letter to the Corinthian church.",
        "yr": "SS3"
      },
      {
        "q": "According to 1 Corinthians 6:19–20, the body is described as:",
        "o": [
          "a temple of the Holy Spirit",
          "an unimportant vessel",
          "a source of shame",
          "a burden to be ignored"
        ],
        "a": 0,
        "e": "This underlines the call to purity.",
        "h": "A sacred dwelling place.",
        "yr": "SS3"
      },
      {
        "q": "Hebrews 13:4 teaches that the marriage bed should be:",
        "o": [
          "ignored entirely",
          "a source of shame",
          "kept pure",
          "treated casually"
        ],
        "a": 2,
        "e": "Marriage and sexual faithfulness are honoured.",
        "h": "Purity within marriage.",
        "yr": "SS3"
      },
      {
        "q": "A direct consequence of immoral sexual living mentioned in this topic is:",
        "o": [
          "increased risk of HIV and other infections",
          "improved health",
          "guaranteed wealth",
          "stronger family bonds"
        ],
        "a": 0,
        "e": "Immoral living increases health risks.",
        "h": "A negative health outcome.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a preventive measure against HIV?",
        "o": [
          "Abstinence and faithfulness in marriage",
          "Sharing needles",
          "Avoiding all blood tests",
          "Ignoring medical advice"
        ],
        "a": 0,
        "e": "These reduce the risk of exposure.",
        "h": "Sexual purity reduces risk.",
        "yr": "SS3"
      },
      {
        "q": "Voluntary Counselling and Testing (VCT) helps individuals to:",
        "o": [
          "ignore their health",
          "avoid all medical facilities",
          "know their HIV status",
          "increase their risk of infection"
        ],
        "a": 2,
        "e": "It allows early detection and informed decisions.",
        "h": "Knowing one's status.",
        "yr": "SS3"
      },
      {
        "q": "Screening of blood before transfusion is important because it:",
        "o": [
          "prevents transmission of HIV through infected blood",
          "has no effect on disease transmission",
          "increases the cost of healthcare only",
          "is only needed for minor surgeries"
        ],
        "a": 0,
        "e": "It is a key preventive measure.",
        "h": "A safeguard against infection.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is NOT a common mode of HIV transmission?",
        "o": [
          "Sharing food or utensils",
          "Unprotected sex",
          "Mother-to-child transmission",
          "Contaminated blood transfusion"
        ],
        "a": 0,
        "e": "Casual contact does not transmit HIV.",
        "h": "This one is a myth, not a fact.",
        "yr": "SS3"
      },
      {
        "q": "The use of sterile medical equipment helps to:",
        "o": [
          "replace the need for testing",
          "prevent the spread of HIV in healthcare settings",
          "increase infection risk",
          "have no impact on transmission"
        ],
        "a": 1,
        "e": "Unsterilised equipment can transmit blood-borne infections.",
        "h": "Cleanliness prevents spread.",
        "yr": "SS3"
      },
      {
        "q": "According to biblical teaching, sexual purity is linked to:",
        "o": [
          "avoiding all human relationships",
          "rejecting marriage entirely",
          "ignoring health concerns",
          "honouring God with one's body"
        ],
        "a": 3,
        "e": "The body belongs to God and should be honoured.",
        "h": "A spiritual principle about the body.",
        "yr": "SS3"
      },
      {
        "q": "Faithfulness within marriage, as taught in Hebrews 13:4, helps to:",
        "o": [
          "weaken family bonds",
          "increase social isolation",
          "reduce the risk of HIV transmission",
          "have no impact on health"
        ],
        "a": 2,
        "e": "Marital faithfulness limits exposure to infection.",
        "h": "A protective behaviour.",
        "yr": "SS3"
      },
      {
        "q": "Which of these consequences of immoral living is primarily spiritual?",
        "o": [
          "Guilt and separation from God",
          "Physical illness only",
          "Financial loss only",
          "Loss of property only"
        ],
        "a": 0,
        "e": "Immorality affects one's relationship with God.",
        "h": "A spiritual, not just physical, consequence.",
        "yr": "SS3"
      },
      {
        "q": "The main biblical message regarding HIV prevention in this topic centres on:",
        "o": [
          "ignoring health education",
          "sexual purity and faithful living",
          "rejecting modern science",
          "avoiding all medical treatment"
        ],
        "a": 1,
        "e": "Purity and faithfulness align with reducing risk.",
        "h": "A moral and practical teaching.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "HIV/AIDS II",
    "topicCode": "SS3-CRS-03",
    "module": "Health, Morality and Christian Living",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">Beyond the biological facts of HIV/AIDS, Christians must also address the myths that fuel stigma, and respond to those affected with compassion rather than condemnation.</div><h3 class=\"learn-subheading\">1. Myths and Misconceptions about HIV/AIDS</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Myth</th><th>Fact</th></tr></thead><tbody><tr><td>HIV can be spread by hugging, shaking hands or sharing food/utensils</td><td>HIV is not spread by casual, everyday contact</td></tr><tr><td>Mosquito bites spread HIV</td><td>Mosquitoes do not transmit HIV</td></tr><tr><td>A person with HIV always looks visibly sick</td><td>A person can carry HIV for years without visible symptoms</td></tr><tr><td>HIV only affects certain groups of people</td><td>Anyone, regardless of age, gender or status, can contract HIV</td></tr><tr><td>AIDS is a death sentence with no way to manage it</td><td>With antiretroviral therapy (ART), people living with HIV can live long, productive lives</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Effects on Individuals, Families and Society</h3><ul style=\"margin-left:20px;\"><li>Individuals: physical weakening, psychological trauma, and discrimination.</li><li>Families: loss of breadwinners, orphaned children, and financial strain from medical care.</li><li>Society: loss of a productive workforce, strain on healthcare systems, and the social cost of stigma.</li></ul><h3 class=\"learn-subheading\">3. Role of Christians in Showing Care and Compassion</h3><p class=\"learn-p\">Christians are called to love their neighbours as themselves (Mark 12:31) and to care for the sick, as Jesus demonstrated throughout His ministry by touching and healing those others avoided, including lepers. Christians should support people living with HIV through acceptance, encouragement, practical help, and by combating stigma and discrimination within the church and community.</p><h3 class=\"learn-subheading\">4. Biblical Support for Healthy Living</h3><p class=\"learn-p\">1 Corinthians 6:19–20 reminds believers that their bodies are temples of the Holy Spirit and should be honoured through healthy, disciplined living. 3 John 1:2 expresses the wish that believers may prosper and be in good health, affirming that physical wellbeing matters to God alongside spiritual health.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Summary:</strong> The Christian response to HIV/AIDS should combine biblical standards of purity with compassion, correct information, and active care for those affected.</div>",
    "questions": [
      {
        "q": "Which of these is a myth about HIV/AIDS?",
        "o": [
          "HIV can be spread by hugging or sharing food",
          "HIV can be spread through contaminated blood",
          "HIV can be spread from mother to child",
          "HIV can be spread through unprotected sex"
        ],
        "a": 0,
        "e": "Casual contact does not transmit HIV.",
        "h": "This one is false.",
        "yr": "SS3"
      },
      {
        "q": "The fact about mosquito bites and HIV is that:",
        "o": [
          "mosquitoes do not transmit HIV",
          "only certain mosquitoes spread HIV",
          "mosquitoes are a major cause of HIV spread",
          "mosquito bites guarantee infection"
        ],
        "a": 0,
        "e": "This is a common but false belief.",
        "h": "Insects are not a transmission route.",
        "yr": "SS3"
      },
      {
        "q": "A person living with HIV:",
        "o": [
          "dies within months of infection",
          "always looks visibly sick immediately",
          "can appear healthy for years without visible symptoms",
          "cannot live a normal life at all"
        ],
        "a": 2,
        "e": "HIV can be asymptomatic for a long period.",
        "h": "Appearance can be misleading.",
        "yr": "SS3"
      },
      {
        "q": "Which statement about who can contract HIV is TRUE?",
        "o": [
          "Only poor people can contract HIV",
          "Only certain social groups can contract HIV",
          "Only adults can contract HIV",
          "Anyone, regardless of age or status, can contract HIV"
        ],
        "a": 3,
        "e": "HIV does not discriminate by social group.",
        "h": "It can affect anyone.",
        "yr": "SS3"
      },
      {
        "q": "Antiretroviral therapy (ART) allows people living with HIV to:",
        "o": [
          "be cured completely of the virus",
          "avoid all medical care",
          "never need any treatment",
          "live long, productive lives"
        ],
        "a": 3,
        "e": "ART manages the virus, though it does not cure it.",
        "h": "Improves quality and length of life.",
        "yr": "SS3"
      },
      {
        "q": "A major effect of HIV/AIDS on families is:",
        "o": [
          "increased family wealth",
          "loss of breadwinners and financial strain",
          "no effect on daily life",
          "stronger family unity in all cases"
        ],
        "a": 1,
        "e": "Illness can remove income earners from the household.",
        "h": "Economic impact on the family.",
        "yr": "SS3"
      },
      {
        "q": "A major effect of HIV/AIDS on society is:",
        "o": [
          "strain on healthcare systems",
          "reduced need for healthcare",
          "increased national wealth",
          "improved workforce productivity"
        ],
        "a": 0,
        "e": "Treating many patients burdens health infrastructure.",
        "h": "Pressure on hospitals and clinics.",
        "yr": "SS3"
      },
      {
        "q": "Orphaned children are often a consequence of HIV/AIDS at the:",
        "o": [
          "religious level only",
          "international level only",
          "family level",
          "governmental level only"
        ],
        "a": 2,
        "e": "Loss of parents directly affects the family.",
        "h": "Impact on the household.",
        "yr": "SS3"
      },
      {
        "q": "According to Mark 12:31, Christians are called to:",
        "o": [
          "judge those who are ill",
          "love their neighbours as themselves",
          "avoid all sick people",
          "ignore the needs of others"
        ],
        "a": 1,
        "e": "This is the second great commandment.",
        "h": "A command about loving others.",
        "yr": "SS3"
      },
      {
        "q": "Jesus' ministry to lepers demonstrates:",
        "o": [
          "compassion toward those others avoided",
          "indifference to suffering",
          "avoidance of physical touch",
          "rejection of the sick"
        ],
        "a": 0,
        "e": "He touched and healed those considered untouchable.",
        "h": "A model of compassionate care.",
        "yr": "SS3"
      },
      {
        "q": "Christians are encouraged to combat stigma against people living with HIV through:",
        "o": [
          "public shaming",
          "acceptance and encouragement",
          "silence and avoidance",
          "isolation and rejection"
        ],
        "a": 1,
        "e": "Love and support counter stigma.",
        "h": "Positive, welcoming action.",
        "yr": "SS3"
      },
      {
        "q": "1 Corinthians 6:19–20 encourages believers to honour God through:",
        "o": [
          "ignoring their bodies",
          "healthy, disciplined living",
          "excessive indulgence",
          "reckless behaviour"
        ],
        "a": 1,
        "e": "The body is described as a temple to be honoured.",
        "h": "Care for the body as God's temple.",
        "yr": "SS3"
      },
      {
        "q": "3 John 1:2 expresses a wish for believers to:",
        "o": [
          "suffer for their faith",
          "remain in poverty",
          "prosper and be in good health",
          "avoid all wealth"
        ],
        "a": 2,
        "e": "It links spiritual and physical wellbeing.",
        "h": "A blessing for wellness.",
        "yr": "SS3"
      },
      {
        "q": "The psychological effect of HIV/AIDS on individuals often includes:",
        "o": [
          "increased self-esteem always",
          "guaranteed social acceptance",
          "trauma and discrimination",
          "improved mental health"
        ],
        "a": 2,
        "e": "Stigma can cause significant emotional harm.",
        "h": "Negative mental health impact.",
        "yr": "SS3"
      },
      {
        "q": "Supporting people living with HIV with practical help is an example of:",
        "o": [
          "a purely political act",
          "a non-Christian value",
          "unnecessary involvement",
          "Christian compassion in action"
        ],
        "a": 3,
        "e": "It reflects the love Christians are called to show.",
        "h": "Living out biblical love.",
        "yr": "SS3"
      },
      {
        "q": "The loss of a productive workforce due to HIV/AIDS affects:",
        "o": [
          "only the infected individual",
          "the broader economy and society",
          "only religious institutions",
          "only foreign nations"
        ],
        "a": 1,
        "e": "Illness reduces available labour in the economy.",
        "h": "Wider economic consequence.",
        "yr": "SS3"
      },
      {
        "q": "Correct information about HIV/AIDS helps to reduce:",
        "o": [
          "stigma and discrimination",
          "compassion for the sick",
          "the number of HIV tests available",
          "access to treatment"
        ],
        "a": 0,
        "e": "Education combats myths that fuel stigma.",
        "h": "Knowledge reduces prejudice.",
        "yr": "SS3"
      },
      {
        "q": "The Christian response to HIV/AIDS should combine biblical purity with:",
        "o": [
          "silence and avoidance",
          "blame and shame",
          "judgment and isolation",
          "compassion and correct information"
        ],
        "a": 3,
        "e": "Both truth and love are needed.",
        "h": "A balanced, loving response.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best reflects a biblical view on caring for the sick?",
        "o": [
          "Avoiding all contact with the sick",
          "Blaming the sick for their condition",
          "Showing love and practical support, as Jesus did",
          "Ignoring their physical needs"
        ],
        "a": 2,
        "e": "Jesus modelled active compassion.",
        "h": "Following Christ's example.",
        "yr": "SS3"
      },
      {
        "q": "The overall message of this topic is that the church should respond to HIV/AIDS with:",
        "o": [
          "total avoidance of the issue",
          "complete silence",
          "truth, compassion and support rather than judgment",
          "harsh condemnation only"
        ],
        "a": 2,
        "e": "A balanced, caring response is called for.",
        "h": "Love combined with truth.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Independence and Self-Actualization",
    "topicCode": "SS3-CRS-04",
    "module": "Christian Living and National Life",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Independence</span> is the state of being free from control by others and able to make one's own decisions, while <span class=\"learn-keyword\">self-actualization</span> is the process of realising and fulfilling one's full potential and abilities.</div><h3 class=\"learn-subheading\">1. Meaning of Independence</h3><p class=\"learn-p\">Independence, in personal life, means being self-governing, responsible, and able to make sound decisions and provide for oneself, rather than being wholly dependent on others.</p><h3 class=\"learn-subheading\">2. Self-Reliance and Personal Responsibility</h3><ul style=\"margin-left:20px;\"><li>Self-reliance means trusting in one's own abilities, skills and effort to meet one's needs.</li><li>Personal responsibility means taking ownership of one's choices, actions and their consequences.</li><li>Both qualities are essential for a young person transitioning into adult life and the workforce.</li></ul><h3 class=\"learn-subheading\">3. Ways of Achieving Self-Actualization</h3><ul style=\"margin-left:20px;\"><li>Discovering and developing one's God-given talents and gifts.</li><li>Setting clear, achievable goals and working diligently toward them.</li><li>Acquiring relevant knowledge, skills and education.</li><li>Maintaining discipline, perseverance and a positive attitude.</li><li>Seeking wise counsel and mentorship.</li><li>Depending on God's guidance through prayer and His word.</li></ul><h3 class=\"learn-subheading\">4. Biblical Examples of Self-Reliant Individuals</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Example</th><th>How they showed self-reliance</th></tr></thead><tbody><tr><td>Joseph (Genesis 37–45)</td><td>Rose from slavery and imprisonment to become a ruler in Egypt through diligence, integrity and trust in God</td></tr><tr><td>Ruth (Book of Ruth)</td><td>Worked diligently gleaning in the fields to provide for herself and Naomi</td></tr><tr><td>Nehemiah (Book of Nehemiah)</td><td>Took personal initiative and led the rebuilding of Jerusalem's walls despite opposition</td></tr><tr><td>The Proverbs 31 woman</td><td>Praised for her industry, business sense and care for her household</td></tr></tbody></table></div><h3 class=\"learn-subheading\">5. Importance of Depending on God</h3><p class=\"learn-p\">While self-reliance is important, Scripture also teaches that true success ultimately depends on God's guidance and blessing (Proverbs 3:5–6, Philippians 4:13). Christians are to balance diligent personal effort with humble trust in God, recognising that human ability alone is not sufficient.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Be able to explain how Joseph's life illustrates BOTH self-reliance and dependence on God.</div>",
    "questions": [
      {
        "q": "Independence, in personal life, means being:",
        "o": [
          "dependent entirely on parents forever",
          "completely isolated from others",
          "unable to work with others",
          "self-governing and able to make sound decisions"
        ],
        "a": 3,
        "e": "It involves responsible self-direction.",
        "h": "Making one's own sound choices.",
        "yr": "SS3"
      },
      {
        "q": "Self-actualization refers to:",
        "o": [
          "depending entirely on others for success",
          "realising and fulfilling one's full potential",
          "rejecting all forms of education",
          "avoiding all personal goals"
        ],
        "a": 1,
        "e": "It is about reaching one's capabilities.",
        "h": "Fulfilling potential.",
        "yr": "SS3"
      },
      {
        "q": "Self-reliance means trusting in:",
        "o": [
          "chance occurrences only",
          "luck alone",
          "other people's effort only",
          "one's own abilities, skills and effort"
        ],
        "a": 3,
        "e": "It emphasises personal capability.",
        "h": "Belief in one's own capacity.",
        "yr": "SS3"
      },
      {
        "q": "Personal responsibility involves:",
        "o": [
          "ignoring the outcomes of actions",
          "avoiding all decision-making",
          "blaming others for one's mistakes",
          "taking ownership of one's choices and their consequences"
        ],
        "a": 3,
        "e": "It means accepting accountability.",
        "h": "Owning one's actions.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a way of achieving self-actualization?",
        "o": [
          "Discovering and developing one's God-given talents",
          "Ignoring one's abilities",
          "Depending solely on luck",
          "Avoiding all personal goals"
        ],
        "a": 0,
        "e": "Using one's gifts is key to growth.",
        "h": "Developing inherent abilities.",
        "yr": "SS3"
      },
      {
        "q": "Setting clear, achievable goals is important for self-actualization because it:",
        "o": [
          "removes the need for effort",
          "eliminates all challenges",
          "guarantees instant success",
          "provides direction for personal growth"
        ],
        "a": 3,
        "e": "Goals guide purposeful action.",
        "h": "Direction and focus.",
        "yr": "SS3"
      },
      {
        "q": "Which biblical figure rose from slavery to become a ruler in Egypt?",
        "o": [
          "Nehemiah",
          "Job",
          "Ruth",
          "Joseph"
        ],
        "a": 3,
        "e": "His story is found in Genesis 37–45.",
        "h": "Sold by his brothers, later a ruler.",
        "yr": "SS3"
      },
      {
        "q": "Joseph's rise to power illustrates:",
        "o": [
          "diligence, integrity and trust in God",
          "laziness and deceit",
          "complete reliance on luck",
          "avoidance of hard work"
        ],
        "a": 0,
        "e": "He remained faithful through hardship.",
        "h": "Character traits that led to success.",
        "yr": "SS3"
      },
      {
        "q": "Ruth demonstrated self-reliance by:",
        "o": [
          "refusing to work at all",
          "abandoning her responsibilities",
          "working diligently gleaning in the fields",
          "depending solely on others for food"
        ],
        "a": 2,
        "e": "She provided for herself and Naomi through labour.",
        "h": "Her industrious work in the fields.",
        "yr": "SS3"
      },
      {
        "q": "Nehemiah showed self-reliance and initiative by:",
        "o": [
          "leading the rebuilding of Jerusalem's walls despite opposition",
          "relying entirely on others to act",
          "avoiding all leadership roles",
          "giving up when faced with challenges"
        ],
        "a": 0,
        "e": "He took personal initiative for a major project.",
        "h": "His determined leadership.",
        "yr": "SS3"
      },
      {
        "q": "The Proverbs 31 woman is praised for her:",
        "o": [
          "industry, business sense and care for her household",
          "avoidance of all responsibility",
          "dependence on others for all needs",
          "laziness and neglect of duties"
        ],
        "a": 0,
        "e": "She exemplifies diligent, capable living.",
        "h": "A model of industrious character.",
        "yr": "SS3"
      },
      {
        "q": "According to Proverbs 3:5–6, believers should:",
        "o": [
          "trust in the Lord with all their heart",
          "ignore wisdom from Scripture",
          "avoid seeking God's guidance",
          "rely solely on their own understanding"
        ],
        "a": 0,
        "e": "This passage calls for trust in God's guidance.",
        "h": "Trusting God above self alone.",
        "yr": "SS3"
      },
      {
        "q": "Philippians 4:13 teaches that believers can do all things through:",
        "o": [
          "luck and chance",
          "their own willpower alone",
          "other people's help alone",
          "Christ who strengthens them"
        ],
        "a": 3,
        "e": "Strength comes from a relationship with Christ.",
        "h": "A source of empowerment beyond self.",
        "yr": "SS3"
      },
      {
        "q": "Balancing self-reliance with dependence on God means:",
        "o": [
          "relying only on oneself with no faith",
          "relying only on God with no personal effort",
          "working diligently while trusting God's guidance",
          "avoiding all effort and all faith"
        ],
        "a": 2,
        "e": "Both human effort and divine trust matter.",
        "h": "A combination of effort and faith.",
        "yr": "SS3"
      },
      {
        "q": "Which quality is essential for a young person transitioning into adulthood?",
        "o": [
          "Self-reliance and personal responsibility",
          "Rejection of all guidance",
          "Avoidance of all decision-making",
          "Complete dependence on parents forever"
        ],
        "a": 0,
        "e": "These qualities prepare one for independent life.",
        "h": "Readiness for adult responsibilities.",
        "yr": "SS3"
      },
      {
        "q": "Acquiring relevant knowledge and skills supports self-actualization by:",
        "o": [
          "making personal growth unnecessary",
          "equipping a person to fulfil their potential",
          "replacing the need for effort",
          "guaranteeing instant wealth"
        ],
        "a": 1,
        "e": "Education and skill-building enable growth.",
        "h": "Preparation for achieving goals.",
        "yr": "SS3"
      },
      {
        "q": "Mentorship and wise counsel contribute to self-actualization by:",
        "o": [
          "replacing the need for personal effort",
          "providing guidance and support for personal growth",
          "guaranteeing immediate success",
          "discouraging independent thought"
        ],
        "a": 1,
        "e": "Wise guidance helps one grow effectively.",
        "h": "External support aiding growth.",
        "yr": "SS3"
      },
      {
        "q": "Discipline and perseverance are important for self-actualization because they:",
        "o": [
          "eliminate all challenges instantly",
          "are unnecessary for success",
          "replace the need for talent",
          "help one overcome obstacles toward a goal"
        ],
        "a": 3,
        "e": "Consistency helps achieve long-term goals.",
        "h": "Persisting through difficulty.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best summarises the Christian view of independence and self-actualization?",
        "o": [
          "Avoidance of goals and personal growth",
          "Total dependence on others with no personal effort",
          "Complete self-sufficiency with no need for God",
          "Diligent personal effort combined with trust in God"
        ],
        "a": 3,
        "e": "Scripture balances human effort with divine reliance.",
        "h": "A balanced approach.",
        "yr": "SS3"
      },
      {
        "q": "Job security and advancement, according to this topic's biblical examples, often result from:",
        "o": [
          "avoidance of responsibility",
          "diligence and integrity over time",
          "luck and shortcuts",
          "deceit and manipulation"
        ],
        "a": 1,
        "e": "Joseph and others succeeded through consistent character.",
        "h": "Long-term faithful effort.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Dignity of Labour",
    "topicCode": "SS3-CRS-05",
    "module": "Christian Living and National Life",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">The <span class=\"learn-keyword\">dignity of labour</span> is the belief that all honest work, whatever its nature, deserves respect, and that work itself is valuable and worthy, not shameful.</div><h3 class=\"learn-subheading\">1. Meaning of Labour</h3><p class=\"learn-p\">Labour refers to any physical or mental effort or work done, usually in exchange for payment or to accomplish a task, whether in farming, trading, teaching, cleaning or any other honest occupation.</p><h3 class=\"learn-subheading\">2. Biblical View on Work</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Passage</th><th>Teaching</th></tr></thead><tbody><tr><td>Genesis 2:15</td><td>God placed Adam in the Garden of Eden to work it and take care of it — work existed even before the Fall, as part of God's original design for humanity</td></tr><tr><td>Proverbs 22:29</td><td>A skilled, diligent worker will stand before kings, not before obscure men — diligence in one's work leads to honour and advancement</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Importance of Dignity in Labour</h3><ul style=\"margin-left:20px;\"><li>It affirms that no honest job is beneath a person's worth.</li><li>It encourages excellence and diligence in whatever work one does.</li><li>It builds self-respect and the respect of others.</li><li>It contributes to personal and national economic development.</li><li>It reflects good stewardship of the abilities God has given.</li></ul><h3 class=\"learn-subheading\">4. Consequences of Idleness</h3><ul style=\"margin-left:20px;\"><li>Poverty and lack (Proverbs 10:4, 'Lazy hands make for poverty').</li><li>Loss of self-respect and purpose.</li><li>Increased temptation toward dishonest means of survival, including crime.</li><li>Burden on family and society.</li><li>Wasted God-given talents and time.</li></ul><h3 class=\"learn-subheading\">5. Relevance of Honest Work in Contemporary Society</h3><p class=\"learn-p\">In today's society, where many seek quick, dishonest wealth, the Christian teaching on the dignity of labour calls believers to value patient, honest work over shortcuts, recognising that true and lasting success comes through diligence and integrity.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Summary:</strong> Work is not a curse but part of God's original plan for humanity; all honest labour deserves dignity and respect.</div>",
    "questions": [
      {
        "q": "Labour is best defined as:",
        "o": [
          "a punishment for wrongdoing",
          "only unpaid household chores",
          "an activity only for the poor",
          "physical or mental effort or work done, usually for payment or to accomplish a task"
        ],
        "a": 3,
        "e": "It covers all honest occupations.",
        "h": "Effort applied to accomplish something.",
        "yr": "SS3"
      },
      {
        "q": "According to Genesis 2:15, God placed Adam in the garden to:",
        "o": [
          "work it and take care of it",
          "rule over Eve alone",
          "rest permanently without duty",
          "avoid all responsibility"
        ],
        "a": 0,
        "e": "Work existed before the Fall, as part of God's design.",
        "h": "A task given before sin entered the world.",
        "yr": "SS3"
      },
      {
        "q": "Genesis 2:15 shows that work is:",
        "o": [
          "a curse that began with sin",
          "only for those outside Eden",
          "unnecessary for believers",
          "part of God's original design for humanity"
        ],
        "a": 3,
        "e": "Work predates the Fall in the biblical account.",
        "h": "Present from the very beginning.",
        "yr": "SS3"
      },
      {
        "q": "Proverbs 22:29 teaches that a skilled, diligent worker will:",
        "o": [
          "lose all opportunities",
          "remain obscure and unnoticed",
          "stand before kings",
          "be punished for working hard"
        ],
        "a": 2,
        "e": "Diligence leads to honour and advancement.",
        "h": "A reward for excellence in work.",
        "yr": "SS3"
      },
      {
        "q": "The dignity of labour teaches that:",
        "o": [
          "manual labour is shameful",
          "only wealthy people deserve respect",
          "only white-collar jobs have value",
          "no honest job is beneath a person's worth"
        ],
        "a": 3,
        "e": "All honest work deserves respect.",
        "h": "Respect for all legitimate work.",
        "yr": "SS3"
      },
      {
        "q": "Dignity in labour encourages believers to work with:",
        "o": [
          "dishonesty for quick gain",
          "excellence and diligence",
          "resentment and complaint",
          "laziness and carelessness"
        ],
        "a": 1,
        "e": "Diligence reflects good stewardship.",
        "h": "Doing one's best in any task.",
        "yr": "SS3"
      },
      {
        "q": "A consequence of idleness mentioned in this topic is:",
        "o": [
          "stronger family bonds",
          "guaranteed wealth",
          "increased self-respect",
          "poverty and lack"
        ],
        "a": 3,
        "e": "Proverbs links laziness to poverty.",
        "h": "A negative outcome of avoiding work.",
        "yr": "SS3"
      },
      {
        "q": "Proverbs 10:4 teaches that:",
        "o": [
          "hard work always leads to failure",
          "work is unnecessary for success",
          "laziness brings great wealth",
          "lazy hands lead to poverty"
        ],
        "a": 3,
        "e": "Diligence, not laziness, brings provision.",
        "h": "The link between effort and outcome.",
        "yr": "SS3"
      },
      {
        "q": "Idleness can lead a person toward:",
        "o": [
          "increased productivity",
          "dishonest means of survival, including crime",
          "stronger moral character",
          "greater discipline"
        ],
        "a": 1,
        "e": "Lack of honest work can tempt one toward wrongdoing.",
        "h": "A negative behavioural consequence.",
        "yr": "SS3"
      },
      {
        "q": "Valuing honest work over quick, dishonest wealth reflects:",
        "o": [
          "an outdated and irrelevant idea",
          "a rejection of all ambition",
          "a purely secular economic theory",
          "the biblical teaching on dignity of labour"
        ],
        "a": 3,
        "e": "Scripture calls for patient, honest effort.",
        "h": "A faith-based view on work ethic.",
        "yr": "SS3"
      },
      {
        "q": "The dignity of labour contributes to national development by:",
        "o": [
          "encouraging productivity and economic growth",
          "promoting unemployment",
          "discouraging all forms of work",
          "increasing dependency on others"
        ],
        "a": 0,
        "e": "Diligent work benefits the wider economy.",
        "h": "Positive impact on the nation.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following best reflects good stewardship of abilities, according to this topic?",
        "o": [
          "Wasting time without purpose",
          "Working diligently with the skills God has given",
          "Relying solely on others for provision",
          "Hiding one's talents and avoiding work"
        ],
        "a": 1,
        "e": "Stewardship means using what God has given well.",
        "h": "Responsible use of God-given ability.",
        "yr": "SS3"
      },
      {
        "q": "Self-respect, according to this topic, is linked to:",
        "o": [
          "rejection of honest work",
          "dependence on others for identity",
          "dignity in labour",
          "avoidance of all responsibility"
        ],
        "a": 2,
        "e": "Taking pride in honest work builds self-respect.",
        "h": "A positive outcome of diligent work.",
        "yr": "SS3"
      },
      {
        "q": "The relevance of honest work in contemporary society is emphasised because many people today:",
        "o": [
          "avoid all forms of ambition",
          "reject material success entirely",
          "value patience and integrity above all",
          "seek quick, dishonest wealth"
        ],
        "a": 3,
        "e": "The topic contrasts honest labour with shortcuts.",
        "h": "A modern temptation addressed by this teaching.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best completes the biblical teaching that work is NOT a result of sin?",
        "o": [
          "Work began only after Adam and Eve sinned",
          "Work is a punishment given by God",
          "Work existed in the Garden of Eden before the Fall",
          "Work has no spiritual significance"
        ],
        "a": 2,
        "e": "Genesis 2:15 places work before the Fall.",
        "h": "Timing in the Genesis narrative.",
        "yr": "SS3"
      },
      {
        "q": "A believer who takes pride in doing a cleaning job well is demonstrating:",
        "o": [
          "laziness",
          "idleness",
          "dishonesty",
          "the dignity of labour"
        ],
        "a": 3,
        "e": "All honest work, done well, has dignity.",
        "h": "Respect for one's task, however humble.",
        "yr": "SS3"
      },
      {
        "q": "Diligence in work, according to Proverbs 22:29, leads to:",
        "o": [
          "failure and poverty",
          "honour and advancement",
          "isolation from others",
          "shame and obscurity"
        ],
        "a": 1,
        "e": "Skilled, diligent workers are elevated.",
        "h": "A positive career outcome.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best reflects a Christian work ethic?",
        "o": [
          "Working only when supervised",
          "Avoiding effort unless paid extra",
          "Taking credit for others' work",
          "Working honestly and diligently in whatever task is given"
        ],
        "a": 3,
        "e": "Christian labour is marked by integrity and effort.",
        "h": "Honest, consistent diligence.",
        "yr": "SS3"
      },
      {
        "q": "The burden placed on family and society by idleness includes:",
        "o": [
          "greater social harmony",
          "reduced need for support",
          "increased national productivity",
          "increased dependency and strain on others"
        ],
        "a": 3,
        "e": "Idle individuals often rely on others for support.",
        "h": "Negative social consequence.",
        "yr": "SS3"
      },
      {
        "q": "Overall, this topic teaches that work should be viewed as:",
        "o": [
          "a curse to be avoided",
          "irrelevant to Christian faith",
          "beneath those with status",
          "valuable and worthy of respect, not shameful"
        ],
        "a": 3,
        "e": "Labour has inherent dignity in Christian teaching.",
        "h": "The central theme of the topic.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Christian Attitude to Corruption",
    "topicCode": "SS3-CRS-06",
    "module": "Christian Living and National Life",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Corruption</span> is the dishonest or unethical use of power, position or resources, especially for personal gain, at the expense of others and of society. The Bible strongly condemns corruption in all its forms.</div><h3 class=\"learn-subheading\">1. Meaning of Corruption</h3><p class=\"learn-p\">Corruption is the abuse of entrusted power or position for private benefit, often involving dishonesty, deception or the betrayal of trust placed in an individual.</p><h3 class=\"learn-subheading\">2. Forms of Corruption</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Form</th><th>Description</th></tr></thead><tbody><tr><td>Bribery</td><td>Offering or accepting money or favours to influence a decision or action unfairly</td></tr><tr><td>Fraud</td><td>Deliberate deception for financial or personal gain</td></tr><tr><td>Nepotism</td><td>Favouring relatives or friends unfairly, especially in appointments or opportunities</td></tr><tr><td>Dishonesty</td><td>Lying, cheating or misrepresenting the truth for personal advantage</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Effects of Corruption on Individuals and Society</h3><ul style=\"margin-left:20px;\"><li>Erosion of trust between citizens and leaders.</li><li>Misallocation of public resources away from development.</li><li>Increased poverty and inequality.</li><li>Weakening of institutions and the rule of law.</li><li>Loss of foreign investment and economic growth.</li><li>Moral decay and the normalisation of dishonesty.</li></ul><h3 class=\"learn-subheading\">4. Biblical Condemnation of Corruption</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Passage</th><th>Teaching</th></tr></thead><tbody><tr><td>Amos 5:12</td><td>God rebukes those who take bribes and deny justice to the needy</td></tr><tr><td>Micah 6:11</td><td>God condemns the use of dishonest scales and deceptive business weights</td></tr><tr><td>Proverbs 17:23</td><td>A wicked person accepts a bribe in secret to pervert the course of justice</td></tr></tbody></table></div><h3 class=\"learn-subheading\">5. Christian Response to Corruption</h3><ul style=\"margin-left:20px;\"><li>Living a life of honesty and integrity in private and public dealings.</li><li>Refusing to give or accept bribes.</li><li>Speaking out against corrupt practices.</li><li>Supporting and promoting transparency and accountability.</li><li>Praying for leaders and the nation to uphold justice.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Match each biblical reference (Amos 5:12, Micah 6:11, Proverbs 17:23) correctly to the form of corruption it condemns.</div>",
    "questions": [
      {
        "q": "Corruption is best defined as:",
        "o": [
          "the dishonest or unethical use of power or position for personal gain",
          "honest service to the public",
          "a form of charity",
          "a legal method of governance"
        ],
        "a": 0,
        "e": "It involves abuse of entrusted trust.",
        "h": "Misuse of power or position.",
        "yr": "SS3"
      },
      {
        "q": "Bribery involves:",
        "o": [
          "donating to charity",
          "paying taxes honestly",
          "giving gifts with no expectation of favour",
          "offering or accepting money or favours to influence a decision unfairly"
        ],
        "a": 3,
        "e": "It corrupts fair decision-making.",
        "h": "An exchange for unfair advantage.",
        "yr": "SS3"
      },
      {
        "q": "Fraud is best described as:",
        "o": [
          "generous giving to others",
          "deliberate deception for financial or personal gain",
          "accidental mistakes in paperwork",
          "honest reporting of facts"
        ],
        "a": 1,
        "e": "It involves intentional dishonesty.",
        "h": "Deliberate deceit for gain.",
        "yr": "SS3"
      },
      {
        "q": "Nepotism refers to:",
        "o": [
          "following due process strictly",
          "rejecting all family involvement",
          "favouring relatives or friends unfairly, especially in appointments",
          "treating all candidates equally"
        ],
        "a": 2,
        "e": "It is a form of unfair favouritism.",
        "h": "Unfair preference for one's own relations.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a form of corruption mentioned in this topic?",
        "o": [
          "Transparency",
          "Dishonesty",
          "Integrity",
          "Accountability"
        ],
        "a": 1,
        "e": "Lying or misrepresenting the truth is corrupt.",
        "h": "The opposite of honesty.",
        "yr": "SS3"
      },
      {
        "q": "A major effect of corruption on society is:",
        "o": [
          "stronger institutions",
          "reduced poverty",
          "misallocation of public resources",
          "improved public trust"
        ],
        "a": 2,
        "e": "Resources meant for development are diverted.",
        "h": "Resources are wasted or stolen.",
        "yr": "SS3"
      },
      {
        "q": "Corruption weakens:",
        "o": [
          "transparency in government",
          "the rule of law and public institutions",
          "trust between citizens and leaders",
          "honest leadership"
        ],
        "a": 1,
        "e": "It undermines the structures meant to serve the public.",
        "h": "Institutional and legal strength.",
        "yr": "SS3"
      },
      {
        "q": "According to Amos 5:12, God rebukes those who:",
        "o": [
          "uphold justice for all",
          "refuse all forms of payment",
          "take bribes and deny justice to the needy",
          "give generously to the poor"
        ],
        "a": 2,
        "e": "This passage condemns corrupt judicial practices.",
        "h": "Injustice linked to bribery.",
        "yr": "SS3"
      },
      {
        "q": "Micah 6:11 condemns the use of:",
        "o": [
          "fair trade agreements",
          "generous giving",
          "dishonest scales and deceptive business weights",
          "honest business practices"
        ],
        "a": 2,
        "e": "It addresses commercial dishonesty.",
        "h": "Cheating in trade and measurement.",
        "yr": "SS3"
      },
      {
        "q": "Proverbs 17:23 describes a wicked person as one who:",
        "o": [
          "gives freely to the poor",
          "accepts a bribe in secret to pervert justice",
          "reports corruption to authorities",
          "refuses all bribes openly"
        ],
        "a": 1,
        "e": "Secret bribery corrupts justice.",
        "h": "Hidden wrongdoing in judicial matters.",
        "yr": "SS3"
      },
      {
        "q": "The Christian response to corruption includes:",
        "o": [
          "participating actively in bribery",
          "supporting dishonest leaders",
          "remaining silent about injustice",
          "refusing to give or accept bribes"
        ],
        "a": 3,
        "e": "Integrity is a key Christian response.",
        "h": "Active refusal to participate.",
        "yr": "SS3"
      },
      {
        "q": "Christians are encouraged to respond to corruption by:",
        "o": [
          "speaking out against corrupt practices",
          "avoiding any involvement in public life",
          "ignoring all forms of injustice",
          "supporting corrupt officials"
        ],
        "a": 0,
        "e": "Believers are called to promote justice.",
        "h": "Active moral opposition.",
        "yr": "SS3"
      },
      {
        "q": "Supporting transparency and accountability is a way Christians can:",
        "o": [
          "increase corrupt practices",
          "combat corruption in society",
          "undermine good governance",
          "weaken public trust further"
        ],
        "a": 1,
        "e": "These values counter corrupt systems.",
        "h": "Promoting openness and responsibility.",
        "yr": "SS3"
      },
      {
        "q": "Corruption leads to loss of foreign investment because it:",
        "o": [
          "improves infrastructure",
          "creates an unstable, untrustworthy business environment",
          "increases investor confidence",
          "strengthens the economy"
        ],
        "a": 1,
        "e": "Investors avoid corrupt, unstable environments.",
        "h": "A negative economic consequence.",
        "yr": "SS3"
      },
      {
        "q": "Which biblical passage specifically addresses corrupt business practices?",
        "o": [
          "Micah 6:11",
          "Proverbs 17:23",
          "Romans 13:1–7",
          "Amos 5:12"
        ],
        "a": 0,
        "e": "It condemns dishonest scales and weights.",
        "h": "Focused on trade and commerce.",
        "yr": "SS3"
      },
      {
        "q": "Which biblical passage specifically addresses bribery and injustice to the needy?",
        "o": [
          "Genesis 2:15",
          "Amos 5:12",
          "Micah 6:11",
          "1 Timothy 6:6–10"
        ],
        "a": 1,
        "e": "God rebukes those who deny justice through bribery.",
        "h": "Focused on judicial injustice.",
        "yr": "SS3"
      },
      {
        "q": "Praying for leaders and the nation is part of the Christian response to:",
        "o": [
          "forgiveness only",
          "civic duty only",
          "honest labour only",
          "corruption"
        ],
        "a": 3,
        "e": "Prayer supports the call for just leadership.",
        "h": "A spiritual response to societal wrongs.",
        "yr": "SS3"
      },
      {
        "q": "Moral decay in society can result from:",
        "o": [
          "honest leadership",
          "transparent governance",
          "strong accountability systems",
          "the normalisation of corrupt practices"
        ],
        "a": 3,
        "e": "When corruption becomes accepted, morality declines.",
        "h": "A consequence of accepting wrongdoing.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best reflects integrity in public or private dealings?",
        "o": [
          "Deceiving others for personal gain",
          "Accepting bribes when convenient",
          "Favouring relatives in appointments",
          "Honesty and refusal to engage in bribery"
        ],
        "a": 3,
        "e": "Integrity means consistent honest conduct.",
        "h": "The opposite of corrupt behaviour.",
        "yr": "SS3"
      },
      {
        "q": "The overall biblical teaching on corruption in this topic is that it is:",
        "o": [
          "only wrong if discovered",
          "strongly condemned in all its forms",
          "a minor issue of little importance",
          "acceptable in moderation"
        ],
        "a": 1,
        "e": "Scripture consistently opposes corrupt practices.",
        "h": "A clear and strong biblical stance.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Forgiveness",
    "topicCode": "SS3-CRS-07",
    "module": "Christian Ethics and Interpersonal Relationships",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Forgiveness</span> is the act of letting go of resentment, anger or the desire for revenge against someone who has wronged you, and is one of the central teachings of Christian living.</div><h3 class=\"learn-subheading\">1. Meaning of Forgiveness</h3><p class=\"learn-p\">Forgiveness means choosing to release an offender from the guilt of their wrong against you, and no longer holding it against them, even though the wrong was real and may have caused genuine hurt.</p><h3 class=\"learn-subheading\">2. Biblical Teachings on Forgiveness</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Passage</th><th>Teaching</th></tr></thead><tbody><tr><td>Matthew 18:21–22</td><td>When Peter asks how often he should forgive, Jesus answers 'seventy times seven,' teaching that forgiveness should be limitless, not merely a fixed number of times</td></tr><tr><td>Luke 15:11–32</td><td>The Parable of the Prodigal Son shows a father who forgives and joyfully welcomes back his wayward son, illustrating God's readiness to forgive those who return to Him</td></tr><tr><td>Colossians 3:13</td><td>Believers are told to bear with one another and forgive, just as the Lord forgave them</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Importance of Forgiveness in Christian Living</h3><ul style=\"margin-left:20px;\"><li>It reflects the character and mercy of God toward us.</li><li>It is a condition for receiving God's own forgiveness (Matthew 6:14–15).</li><li>It restores broken relationships.</li><li>It demonstrates genuine Christian love in action.</li></ul><h3 class=\"learn-subheading\">4. Benefits of Forgiveness to Individuals and Communities</h3><ul style=\"margin-left:20px;\"><li>Emotional and psychological healing; freedom from bitterness and stress.</li><li>Restoration of peace and harmony in relationships.</li><li>Reduced conflict and improved community cohesion.</li><li>Opportunity for reconciliation and renewed trust.</li><li>A positive Christian witness to others.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Summary:</strong> Forgiveness is not optional for the Christian; it is both commanded and modelled by God, and it benefits the one who forgives as much as the one forgiven.</div>",
    "questions": [
      {
        "q": "Forgiveness is best defined as:",
        "o": [
          "demanding full punishment for a wrong",
          "releasing an offender from the guilt of their wrong against you",
          "forgetting that a wrong ever happened",
          "pretending no hurt occurred"
        ],
        "a": 1,
        "e": "It means letting go of the offence, not denying it happened.",
        "h": "Letting go of resentment toward someone.",
        "yr": "SS3"
      },
      {
        "q": "In Matthew 18:21–22, Peter asks Jesus how many times he should forgive:",
        "o": [
          "a stranger who insults him",
          "a government official",
          "his brother who sins against him",
          "an enemy nation"
        ],
        "a": 2,
        "e": "The question concerns forgiving a brother repeatedly.",
        "h": "A question about forgiving someone close.",
        "yr": "SS3"
      },
      {
        "q": "Jesus answers Peter's question about forgiveness by saying he should forgive:",
        "o": [
          "exactly seven times",
          "only three times",
          "seventy times seven",
          "only once"
        ],
        "a": 2,
        "e": "This teaches that forgiveness should be limitless.",
        "h": "A number symbolising unlimited forgiveness.",
        "yr": "SS3"
      },
      {
        "q": "The phrase 'seventy times seven' in Matthew 18:22 teaches that forgiveness should be:",
        "o": [
          "withheld after repeated offences",
          "counted carefully and stopped at 490",
          "limitless, not merely a fixed number of times",
          "given only to close family"
        ],
        "a": 2,
        "e": "It is not meant as a literal count.",
        "h": "Without a strict limit.",
        "yr": "SS3"
      },
      {
        "q": "The Parable of the Prodigal Son is found in:",
        "o": [
          "Matthew 18:21–22",
          "Colossians 3:13",
          "Genesis 2:15",
          "Luke 15:11–32"
        ],
        "a": 3,
        "e": "It tells of a father's forgiveness toward his wayward son.",
        "h": "A well-known parable in Luke's Gospel.",
        "yr": "SS3"
      },
      {
        "q": "In the Parable of the Prodigal Son, the father's reaction to his returning son shows:",
        "o": [
          "indifference and silence",
          "demand for repayment first",
          "anger and rejection",
          "forgiveness and joyful welcome"
        ],
        "a": 3,
        "e": "He welcomes his son back with joy.",
        "h": "A warm, forgiving response.",
        "yr": "SS3"
      },
      {
        "q": "The Parable of the Prodigal Son illustrates:",
        "o": [
          "the importance of punishing wrongdoers",
          "the dangers of leaving home",
          "God's readiness to forgive those who return to Him",
          "God's unwillingness to forgive repeat sinners"
        ],
        "a": 2,
        "e": "It is a picture of divine mercy.",
        "h": "A lesson about God's character.",
        "yr": "SS3"
      },
      {
        "q": "Colossians 3:13 instructs believers to:",
        "o": [
          "avoid all close relationships",
          "seek revenge for past wrongs",
          "hold grudges against those who wrong them",
          "bear with one another and forgive, as the Lord forgave them"
        ],
        "a": 3,
        "e": "Christian forgiveness mirrors God's forgiveness of us.",
        "h": "A command modelled on Christ's example.",
        "yr": "SS3"
      },
      {
        "q": "According to Matthew 6:14–15, receiving God's forgiveness is connected to:",
        "o": [
          "attending church regularly",
          "giving large amounts of money",
          "forgiving others",
          "avoiding all sin completely"
        ],
        "a": 2,
        "e": "Our forgiveness of others relates to receiving God's forgiveness.",
        "h": "A reciprocal spiritual principle.",
        "yr": "SS3"
      },
      {
        "q": "Forgiveness is described as reflecting:",
        "o": [
          "human weakness and failure",
          "an outdated religious custom",
          "the character and mercy of God",
          "a purely legal requirement"
        ],
        "a": 2,
        "e": "It mirrors God's own nature.",
        "h": "A divine attribute shown through believers.",
        "yr": "SS3"
      },
      {
        "q": "One emotional benefit of forgiveness for the person who forgives is:",
        "o": [
          "greater isolation from others",
          "freedom from bitterness and stress",
          "increased anger and resentment",
          "prolonged emotional pain"
        ],
        "a": 1,
        "e": "Forgiveness brings inner healing.",
        "h": "Release from negative emotions.",
        "yr": "SS3"
      },
      {
        "q": "Forgiveness helps restore:",
        "o": [
          "lost property",
          "physical health instantly",
          "financial losses automatically",
          "broken relationships"
        ],
        "a": 3,
        "e": "It repairs trust and connection between people.",
        "h": "Mending relational breaks.",
        "yr": "SS3"
      },
      {
        "q": "A benefit of forgiveness to communities is:",
        "o": [
          "increased division and distrust",
          "more frequent disputes",
          "reduced conflict and improved cohesion",
          "weaker social bonds"
        ],
        "a": 2,
        "e": "Forgiveness fosters peace among people.",
        "h": "A positive social outcome.",
        "yr": "SS3"
      },
      {
        "q": "Forgiveness is described in this topic as:",
        "o": [
          "only for extreme circumstances",
          "irrelevant to daily Christian life",
          "both commanded and modelled by God",
          "optional and unimportant"
        ],
        "a": 2,
        "e": "Scripture both commands and demonstrates forgiveness.",
        "h": "A central, non-optional Christian practice.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best reflects genuine Christian love in action?",
        "o": [
          "Holding grudges indefinitely",
          "Forgiving those who have wronged us",
          "Seeking revenge for past hurts",
          "Avoiding all relationships to prevent hurt"
        ],
        "a": 1,
        "e": "Forgiveness demonstrates love in practice.",
        "h": "An active expression of love.",
        "yr": "SS3"
      },
      {
        "q": "The father in the Parable of the Prodigal Son represents:",
        "o": [
          "a strict, unforgiving judge",
          "an indifferent bystander",
          "God's merciful and forgiving nature",
          "a vengeful ruler"
        ],
        "a": 2,
        "e": "He models how God responds to repentant sinners.",
        "h": "A picture of divine compassion.",
        "yr": "SS3"
      },
      {
        "q": "Bearing with one another, as taught in Colossians 3:13, involves:",
        "o": [
          "demanding perfection from others",
          "patience and forgiveness toward others' faults",
          "avoiding all contact with others",
          "holding onto past offences"
        ],
        "a": 1,
        "e": "It calls for patient, forgiving relationships.",
        "h": "Tolerance combined with forgiveness.",
        "yr": "SS3"
      },
      {
        "q": "Forgiveness benefits individuals by providing:",
        "o": [
          "greater social isolation",
          "emotional and psychological healing",
          "increased guilt and shame",
          "continued resentment"
        ],
        "a": 1,
        "e": "Letting go of hurt supports mental wellbeing.",
        "h": "A healing, restorative effect.",
        "yr": "SS3"
      },
      {
        "q": "According to this topic, forgiveness is NOT:",
        "o": [
          "commanded by Scripture",
          "beneficial to the one who forgives",
          "modelled by God Himself",
          "optional for the Christian"
        ],
        "a": 3,
        "e": "The topic states it is not optional.",
        "h": "What forgiveness is explicitly said not to be.",
        "yr": "SS3"
      },
      {
        "q": "The overall teaching of this topic on forgiveness emphasises:",
        "o": [
          "unlimited forgiveness modelled on God's mercy",
          "forgiveness only for minor offences",
          "forgiveness as a sign of weakness",
          "limited forgiveness after repeated offences"
        ],
        "a": 0,
        "e": "Forgiveness should reflect God's boundless mercy.",
        "h": "The central message about forgiveness.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Perseverance, Wealth and Poverty",
    "topicCode": "SS3-CRS-08",
    "module": "Christian Ethics and Economic Life",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Perseverance</span> is steady persistence in a course of action despite difficulty, while the Bible gives balanced guidance on both the pursuit of <span class=\"learn-keyword\">wealth</span> and the reality of <span class=\"learn-keyword\">poverty</span>.</div><h3 class=\"learn-subheading\">1. Meaning of Perseverance</h3><p class=\"learn-p\">Perseverance is the quality of continuing to work toward a goal or hold firm to one's faith despite obstacles, discouragement or delay.</p><h3 class=\"learn-subheading\">2. Biblical Examples of Perseverance</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Example</th><th>How they persevered</th></tr></thead><tbody><tr><td>Job</td><td>Remained faithful to God despite losing his wealth, children and health, refusing to curse God even in great suffering</td></tr><tr><td>Paul</td><td>Continued preaching the gospel despite beatings, imprisonment, shipwreck and persecution</td></tr><tr><td>Joseph</td><td>Remained faithful to God through slavery and unjust imprisonment before rising to become ruler in Egypt</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Wealth — Meaning, Acquisition and Biblical Guidelines</h3><p class=\"learn-p\">Wealth refers to an abundance of valuable possessions, money or resources. The Bible does not condemn wealth itself but warns against the love of money and wrong attitudes toward it.</p><p class=\"learn-p\"><strong>1 Timothy 6:6–10</strong> teaches that godliness with contentment is great gain, that believers brought nothing into the world and can take nothing out, and that the <em>love</em> of money — not money itself — is a root of all kinds of evil, leading some away from the faith.</p><h3 class=\"learn-subheading\">4. Poverty — Causes and Biblical Response</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Cause of poverty</th><th>Example</th></tr></thead><tbody><tr><td>Laziness or idleness</td><td>Proverbs repeatedly warns against laziness leading to poverty</td></tr><tr><td>Injustice and oppression</td><td>Unfair treatment of the poor by the powerful</td></tr><tr><td>Calamity or misfortune</td><td>Natural disasters, illness or loss beyond one's control</td></tr><tr><td>Poor stewardship</td><td>Mismanagement of available resources</td></tr></tbody></table></div><p class=\"learn-p\"><strong>Acts 4:32–35</strong> describes the early church sharing their possessions so that no one among them was in need. <strong>Proverbs 19:17</strong> teaches that whoever is kind to the poor lends to the Lord, who will reward them for what they have done.</p><h3 class=\"learn-subheading\">5. Christian Attitude to Wealth and Poverty</h3><ul style=\"margin-left:20px;\"><li>Work diligently and persevere through hardship, trusting God.</li><li>Avoid greed and the love of money.</li><li>Be generous and compassionate toward the poor.</li><li>See wealth as a tool for blessing others, not an end in itself.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Remember that 1 Timothy 6:10 says the LOVE of money is a root of evil — not money itself.</div>",
    "questions": [
      {
        "q": "Perseverance is best defined as:",
        "o": [
          "changing goals frequently without reason",
          "avoiding all forms of hardship",
          "giving up quickly when challenges arise",
          "steady persistence in a course of action despite difficulty"
        ],
        "a": 3,
        "e": "It involves continuing despite obstacles.",
        "h": "Persistent effort through hardship.",
        "yr": "SS3"
      },
      {
        "q": "Job is a biblical example of perseverance because he:",
        "o": [
          "avoided all hardship in life",
          "abandoned his faith after losing everything",
          "cursed God when trouble came",
          "remained faithful to God despite losing his wealth, children and health"
        ],
        "a": 3,
        "e": "Job's story is a model of enduring faith.",
        "h": "A figure who suffered greatly yet stayed faithful.",
        "yr": "SS3"
      },
      {
        "q": "Paul demonstrated perseverance by:",
        "o": [
          "avoiding all missionary work",
          "giving up his ministry after the first hardship",
          "continuing to preach despite beatings, imprisonment and persecution",
          "seeking an easy, comfortable life"
        ],
        "a": 2,
        "e": "He endured great suffering for the gospel.",
        "h": "His ongoing ministry despite suffering.",
        "yr": "SS3"
      },
      {
        "q": "Joseph's perseverance is shown through his:",
        "o": [
          "refusal to work while enslaved",
          "abandonment of his faith in hardship",
          "faithfulness during slavery and unjust imprisonment",
          "immediate rise to power with no struggle"
        ],
        "a": 2,
        "e": "He remained faithful before his eventual rise to power.",
        "h": "Endurance through unjust suffering.",
        "yr": "SS3"
      },
      {
        "q": "According to 1 Timothy 6:6–10, believers should pursue:",
        "o": [
          "godliness with contentment",
          "material possessions above all",
          "wealth at any cost",
          "power over others"
        ],
        "a": 0,
        "e": "Paul teaches that godliness with contentment is great gain.",
        "h": "An attitude valued above riches.",
        "yr": "SS3"
      },
      {
        "q": "1 Timothy 6:7 teaches that believers:",
        "o": [
          "should accumulate as much wealth as possible",
          "brought nothing into the world and can take nothing out of it",
          "are guaranteed wealth by God",
          "can keep their possessions after death"
        ],
        "a": 1,
        "e": "This underscores the temporary nature of material wealth.",
        "h": "A statement about life's temporary possessions.",
        "yr": "SS3"
      },
      {
        "q": "According to 1 Timothy 6:10, what is described as a root of all kinds of evil?",
        "o": [
          "Generosity",
          "Hard work",
          "The love of money",
          "Money itself"
        ],
        "a": 2,
        "e": "It is the love of money, not money itself, that is condemned.",
        "h": "Not the money, but the attitude toward it.",
        "yr": "SS3"
      },
      {
        "q": "1 Timothy 6:10 warns that the love of money can lead some away from:",
        "o": [
          "good health",
          "the faith",
          "poverty",
          "perseverance"
        ],
        "a": 1,
        "e": "It can cause spiritual wandering.",
        "h": "A spiritual consequence of greed.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is listed as a cause of poverty in this topic?",
        "o": [
          "Diligent hard work",
          "Wise stewardship",
          "Laziness or idleness",
          "Generosity to others"
        ],
        "a": 2,
        "e": "Proverbs warns against laziness leading to poverty.",
        "h": "A negative behaviour linked to poverty.",
        "yr": "SS3"
      },
      {
        "q": "Injustice and oppression are identified in this topic as a cause of:",
        "o": [
          "perseverance",
          "poverty",
          "wealth",
          "forgiveness"
        ],
        "a": 1,
        "e": "Unfair treatment can trap people in poverty.",
        "h": "A social cause of poverty.",
        "yr": "SS3"
      },
      {
        "q": "Acts 4:32–35 describes the early church as a community that:",
        "o": [
          "rejected all forms of generosity",
          "ignored the needs of the poor",
          "kept all wealth for themselves individually",
          "shared their possessions so no one was in need"
        ],
        "a": 3,
        "e": "They practised communal sharing to meet needs.",
        "h": "An example of generous community living.",
        "yr": "SS3"
      },
      {
        "q": "According to Proverbs 19:17, being kind to the poor is described as:",
        "o": [
          "lending to the Lord",
          "a sign of foolishness",
          "unnecessary for believers",
          "a waste of personal resources"
        ],
        "a": 0,
        "e": "God promises to reward kindness to the poor.",
        "h": "An act God treats as a loan to Himself.",
        "yr": "SS3"
      },
      {
        "q": "Proverbs 19:17 teaches that the Lord will:",
        "o": [
          "reward those who are kind to the poor",
          "ignore acts of generosity",
          "punish those who give to the poor",
          "only reward the wealthy"
        ],
        "a": 0,
        "e": "Generosity to the poor is rewarded by God.",
        "h": "A divine promise tied to kindness.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a Christian attitude toward wealth, according to this topic?",
        "o": [
          "Avoiding greed and the love of money",
          "Pursuing wealth at any moral cost",
          "Rejecting all forms of work",
          "Hoarding wealth without generosity"
        ],
        "a": 0,
        "e": "Wealth should not become an object of greed.",
        "h": "A balanced, godly attitude.",
        "yr": "SS3"
      },
      {
        "q": "Wealth, according to biblical teaching in this topic, should be seen as:",
        "o": [
          "a guarantee of spiritual favour",
          "something to be hidden from others",
          "the ultimate goal of life",
          "a tool for blessing others, not an end in itself"
        ],
        "a": 3,
        "e": "Material resources are meant to serve a greater purpose.",
        "h": "A means rather than the final goal.",
        "yr": "SS3"
      },
      {
        "q": "Poor stewardship is identified in this topic as a cause of:",
        "o": [
          "generosity",
          "forgiveness",
          "poverty",
          "perseverance"
        ],
        "a": 2,
        "e": "Mismanaging resources can lead to lack.",
        "h": "Mishandling available resources.",
        "yr": "SS3"
      },
      {
        "q": "The example of Job teaches believers to persevere especially through:",
        "o": [
          "periods without any challenge",
          "financial success only",
          "suffering and loss",
          "times of ease and comfort only"
        ],
        "a": 2,
        "e": "Job's endurance came amid great hardship.",
        "h": "Enduring difficult circumstances.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best summarises the biblical teaching on wealth in 1 Timothy 6:6–10?",
        "o": [
          "Wealth guarantees spiritual blessing",
          "Money itself is described as evil",
          "Wealth is not condemned, but the love of money is dangerous",
          "All wealth is sinful and must be rejected"
        ],
        "a": 2,
        "e": "The passage distinguishes money from the love of money.",
        "h": "A nuanced, not absolute, teaching.",
        "yr": "SS3"
      },
      {
        "q": "A believer who perseveres through hardship while avoiding greed and showing kindness to the poor is demonstrating:",
        "o": [
          "only partial obedience to Scripture",
          "the balanced Christian attitude taught in this topic",
          "a purely secular value system",
          "an unbiblical approach to life"
        ],
        "a": 1,
        "e": "This reflects the combined teaching on perseverance, wealth and poverty.",
        "h": "Integrating all the topic's key lessons.",
        "yr": "SS3"
      },
      {
        "q": "The overall theme connecting perseverance, wealth and poverty in this topic is:",
        "o": [
          "faithful endurance and generous, godly stewardship of resources",
          "the pursuit of wealth without regard for others",
          "rejection of all material resources",
          "avoidance of all hardship and challenge"
        ],
        "a": 0,
        "e": "The topic calls for perseverance and wise, generous use of resources.",
        "h": "A combined lesson on character and stewardship.",
        "yr": "SS3"
      }
    ]
  }
];
